import { notification, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileService } from '../../services/fileService';

interface Props {
  file: {
    cufi: string;
  };
  onSummaryGenerated: () => void;
}

const fileService = new FileService();

const convertMarkdownToHTML = (text: string): string => {
  const lines = text.split('\n');

  let inList = false;
  let html = '';

  lines.forEach((line) => {
    // Title
    if (/^\*[^*]+\*$/.test(line.trim())) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const titleText = line.trim().replace(/^\*|\*$/g, '');
      html += `<h1 class="text-lg font-bold mb-2">${titleText}</h1>`;
    }
    // Bullet point
    else if (/^- /.test(line.trim())) {
      if (!inList) {
        html += '<ul class="list-disc list-inside mb-2">';
        inList = true;
      }
      const itemText = line.trim().replace(/^- /, '');
      html += `<li>${itemText}</li>`;
    }
    // Row
    else if (line.trim() === '') {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += '<br />';
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `${line}<br />`;
    }
  });

  if (inList) {
    html += '</ul>';
  }

  return html;
};

export const AiSummary = ({ file, onSummaryGenerated }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [htmlSummary, setHtmlSummary] = useState<string>('');

  useEffect(() => {
    fileService
      .aiSummary(file.cufi)
      .then((res) => {
        if (res?.summary) {
          const formattedHtml = convertMarkdownToHTML(res.summary);
          setHtmlSummary(formattedHtml);
          onSummaryGenerated();
        }
      })
      .catch(() => {
        notification.error({
          message: t('errors.genericFetchError', {
            resource: t('common.file'),
          }),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [file.cufi, onSummaryGenerated, t]);

  return (
    <div className="min-w-screen-lg p-4">
      {loading ? (
        <div className="flex w-full flex-col items-center gap-2">
          <Skeleton active paragraph={{ rows: 6 }} />
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : (
        <div
          className="max-h-[60vh] overflow-y-auto pr-2"
          dangerouslySetInnerHTML={{ __html: htmlSummary }}
        />
      )}
    </div>
  );
};

export default AiSummary;
